import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';

import axios from 'axios';
import { getAccessToken } from '../store/actions/auth';
import API_BASE_URL from '../config/baseUrl';
import { formatDisplayUserId } from '../utils/userIdFormat';

// Fetch complete ledger
const fetchCompleteLedger = async function(matchId, matchCompleted, fancyIdList, userIdList) {
  try {
    const token = getAccessToken();
    
    if (!token) {
      return { success: false, error: "No access token found" };
    }

    const response = await axios.post(
      API_BASE_URL + "/report/get-complete-ledger",
      {
        matchId: matchId,
        matchCompleted: matchCompleted,
        fancyIdList: fancyIdList,
        userIdList: userIdList,
        oddsAndSessionBoth: true
      },
      { headers: { Authorization: "Bearer " + token } }
    );

    const data = response.data;

    if (data.status === false) {
      return { success: false, error: data.message || "Failed to fetch ledger" };
    }

    return { success: true, data: data.data };
  } catch (err) {
    const errorMessage = err.response?.data?.message || "Failed to fetch ledger";
    return { success: false, error: errorMessage };
  }
};

const formatAmount = function(value) {
  const numberValue = Number(value);
  if (!Number.isFinite(numberValue)) {
    return '0.00';
  }
  return numberValue.toFixed(2);
};

const getAmountColorClass = function(value) {
  const numberValue = Number(value);
  if (!Number.isFinite(numberValue) || numberValue === 0) return "";
  return numberValue < 0 ? "text-red-600" : "text-green-600";
};

const AmountCell = ({ value, className = "", forceColorClass = "" }) => {
  const colorClass = forceColorClass || getAmountColorClass(value);
  return (
    <td className={`text-center w-20 ${className} ${colorClass}`.trim()}>
      {formatAmount(value)}
    </td>
  );
};

const getValue = function(item, key) {
  if (!item) return undefined;
  if (Object.prototype.hasOwnProperty.call(item, key)) return item[key];
  const altKey = key.replace('SuperMaster', 'SuperAdmin');
  if (altKey !== key && Object.prototype.hasOwnProperty.call(item, altKey)) return item[altKey];
  return item[key] ?? item[altKey];
};

const renderColGroup = function(count, firstWidth = 160, otherWidth = 110) {
  const cols = Array.from({ length: count }, function(_, index) {
    const width = index === 0 ? firstWidth : otherWidth;
    return <col key={index} style={{ width: `${width}px` }} />;
  });

  return <colgroup>{cols}</colgroup>;
};

const getTableMinWidth = function(count, firstWidth = 160, otherWidth = 110) {
  const totalWidth = firstWidth + (count - 1) * otherWidth;
  return `${totalWidth}px`;
};
// Recursive component to render nested ledger structure
const LedgerCard = ({ item, level }) => {
  const ledgerChildren = item.ledgetList || item.ledgerList || [];
  const hasChildren = ledgerChildren.length > 0;
  
  // Determine the name field based on level
  const getNameFor = function(entry) {
    if (entry.adminName) return entry.adminName;
    if (entry.subAdminName) return entry.subAdminName;
    if (entry.superAdminName) return entry.superAdminName;
    if (entry.superMasterName) return entry.superMasterName;
    if (entry.masterName) return entry.masterName;
    if (entry.dealerName) return entry.dealerName;
    if (entry.username) return entry.username;
    if (entry.userId) return entry.userId;
    return '';
  };

  const getCodeFor = function(entry) {
    if (entry.userId) return formatDisplayUserId(entry.userId);
    return '';
  };

  const name = getNameFor(item);
  const code = getCodeFor(item);

  const isClient = !hasChildren;

  // Level titles
  const levelTitles = ['admin', 'subadmin', 'Master', 'Superagent', 'Agent'];
  const totalLabels = ['ADM. TOTAL', 'AD. TOTAL', 'MA. TOTAL', 'SA. TOTAL', 'A. TOTAL'];
  const levelTitle = levelTitles[level] || 'Agent';

  // Grid template columns based on level
  const gridTemplateColumns = level === 0 ? '3fr 1fr 51fr' :
                               level === 1 ? '4fr 1fr 50fr' :
                               level === 2 ? '5fr 1fr 49fr' :
                               level === 3 ? '6fr 1.2fr 49fr' :
                               '7fr 1fr 46.5fr';

  // Background color classes
  const headerBgClass = level === 0 ? 'bg-[#c8e6c9]' :
                        level === 1 ? 'bg-[#b3e5fc]' :
                        level === 2 ? 'bg-[#ffe082]' :
                        level === 3 ? 'bg-[#66bb6a]' :
                        level === 4 ? 'bg-[#ab47bc]' :
                        'bg-[#ffa726]';

  const isAgentLevel = level === levelTitles.length - 1;
  const clientRows = hasChildren ? ledgerChildren : [item];

  const renderClientTable = function(rows, totalsItem) {
    return (
      <div className="w-full">
        <table className="plus-table w-full font-bold" style={{ minWidth: getTableMinWidth(35) }}>
          {renderColGroup(35)}
          <thead>
            <tr className="uppercase text-sm">
              <th colSpan="5" className=""></th>
              <th colSpan="6" className="p-1.5 text-center bg-gray-100" style={{ border: '1px solid grey' }}>Agent PLUS MINUS</th>
              <th colSpan="6" className="p-1.5 text-center bg-gray-100" style={{ border: '1px solid grey' }}>Super Agent PLUS MINUS</th>
              <th colSpan="6" className="p-1.5 text-center bg-gray-100" style={{ border: '1px solid grey' }}>Master PLUS MINUS</th>
              <th colSpan="6" className="p-1.5 text-center bg-gray-100" style={{ border: '1px solid grey' }}>Subadmin PLUS MINUS</th>
              <th colSpan="6" className="p-1.5 text-center bg-gray-100" style={{ border: '1px solid grey' }}>Admin PLUS MINUS</th>
            </tr>
            <tr className="text-[#545454] font-medium uppercase text-sm">
              <th className="p-2 font-bold text-center w-20" style={{ border: '1px solid grey' }}>Client</th>
              <th className="p-2 text-center w-20" style={{ border: '1px solid grey' }}>M AMT</th>
              <th className="p-2 text-center w-20" style={{ border: '1px solid grey' }}>Sess.</th>
              <th className="p-2 text-center w-20" style={{ border: '1px solid grey' }}>TOT.AMT</th>
              <th className="p-2 text-center w-20" style={{ border: '1px solid grey' }}>Net Amt</th>
              <th className="p-2 text-center w-20" style={{ border: '1px solid grey' }}>M Comm</th>
              <th className="p-2 text-center w-20" style={{ border: '1px solid grey' }}>S Comm</th>
              <th className="p-2 text-center w-20" style={{ border: '1px solid grey' }}>T Com</th>
              <th className="p-2 text-center w-20" style={{ border: '1px solid grey' }}>Net Amt</th>
              <th className="p-2 text-center w-20" style={{ border: '1px solid grey' }}>SHR</th>
              <th className="p-2 text-center w-20" style={{ border: '1px solid grey' }}>Final</th>
              <th className="p-2 text-center w-20" style={{ border: '1px solid grey' }}>M Comm</th>
              <th className="p-2 text-center w-20" style={{ border: '1px solid grey' }}>S Comm</th>
              <th className="p-2 text-center w-20" style={{ border: '1px solid grey' }}>T Com</th>
              <th className="p-2 text-center w-20" style={{ border: '1px solid grey' }}>Net Amt</th>
              <th className="p-2 text-center w-20" style={{ border: '1px solid grey' }}>SHR</th>
              <th className="p-2 text-center w-20" style={{ border: '1px solid grey' }}>Final</th>
              <th className="p-2 text-center w-20" style={{ border: '1px solid grey' }}>M Comm</th>
              <th className="p-2 text-center w-20" style={{ border: '1px solid grey' }}>S Comm</th>
              <th className="p-2 text-center w-20" style={{ border: '1px solid grey' }}>T Com</th>
              <th className="p-2 text-center w-20" style={{ border: '1px solid grey' }}>Net Amt</th>
              <th className="p-2 text-center w-20" style={{ border: '1px solid grey' }}>SHR</th>
              <th className="p-2 text-center w-20" style={{ border: '1px solid grey' }}>Final</th>
              <th className="p-2 text-center w-20" style={{ border: '1px solid grey' }}>M Comm</th>
              <th className="p-2 text-center w-20" style={{ border: '1px solid grey' }}>S Comm</th>
              <th className="p-2 text-center w-20" style={{ border: '1px solid grey' }}>T Com</th>
              <th className="p-2 text-center w-20" style={{ border: '1px solid grey' }}>Net Amt</th>
              <th className="p-2 text-center w-20" style={{ border: '1px solid grey' }}>SHR</th>
              <th className="p-2 text-center w-20" style={{ border: '1px solid grey' }}>Final</th>
              <th className="p-2 text-center w-20" style={{ border: '1px solid grey' }}>M Comm</th>
              <th className="p-2 text-center w-20" style={{ border: '1px solid grey' }}>S Comm</th>
              <th className="p-2 text-center w-20" style={{ border: '1px solid grey' }}>T Com</th>
              <th className="p-2 text-center w-20" style={{ border: '1px solid grey' }}>Net Amt</th>
              <th className="p-2 text-center w-20" style={{ border: '1px solid grey' }}>SHR</th>
              <th className="p-2 text-center w-20" style={{ border: '1px solid grey' }}>Final</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(function(rowItem, rowIndex) {
              const rowName = getNameFor(rowItem);
              const rowCode = getCodeFor(rowItem);
              return (
                <tr key={rowItem.userId || rowIndex} className="bg-white border-opacity-10 text-sm">
                  <td className="text-center w-20 font-bold text-black/70">
                    {rowName} <span className="text-blue-500">({rowCode})</span>
                  </td>
                  <AmountCell value={rowItem.matchAmount} />
                  <AmountCell value={rowItem.sessionAmount} />
                  <AmountCell value={rowItem.totalAmoount} className="font-bold" />
                  <AmountCell value={rowItem.netAmountDealer} />
                  <AmountCell value={rowItem.matchCommissionDealer} forceColorClass="text-red-600" />
                  <AmountCell value={rowItem.sessionCommissionDealer} forceColorClass="text-red-600" />
                  <AmountCell value={rowItem.totalCommissionDealer} className="font-bold text-black/70" forceColorClass="text-red-600" />
                  <AmountCell value={rowItem.netAmountDealer} />
                  <AmountCell value={rowItem.shareAmountDealer} />
                  <AmountCell value={rowItem.finalAmountDealer} className="font-bold text-black/70" />
                  <AmountCell value={rowItem.matchCommissionMaster} forceColorClass="text-red-600" />
                  <AmountCell value={rowItem.sessionCommissionMaster} forceColorClass="text-red-600" />
                  <AmountCell value={rowItem.totalCommissionMaster} className="font-bold text-black/70" forceColorClass="text-red-600" />
                  <AmountCell value={rowItem.netAmountMaster} />
                  <AmountCell value={rowItem.shareAmountMaster} />
                  <AmountCell value={rowItem.finalAmountMaster} className="font-bold text-black/70" />
                  <AmountCell value={getValue(rowItem, 'matchCommissionSuperMaster')} forceColorClass="text-red-600" />
                  <AmountCell value={getValue(rowItem, 'sessionCommissionSuperMaster')} forceColorClass="text-red-600" />
                  <AmountCell value={getValue(rowItem, 'totalCommissionSuperMaster')} className="font-bold text-black/70" forceColorClass="text-red-600" />
                  <AmountCell value={getValue(rowItem, 'netAmountSuperMaster')} />
                  <AmountCell value={getValue(rowItem, 'shareAmountSuperMaster')} />
                  <AmountCell value={getValue(rowItem, 'finalAmountSuperMaster')} className="font-bold text-black/70" />
                  <AmountCell value={rowItem.matchCommissionSubAdmin} forceColorClass="text-red-600" />
                  <AmountCell value={rowItem.sessionCommissionSubAdmin} forceColorClass="text-red-600" />
                  <AmountCell value={rowItem.totalCommissionSubAdmin} className="font-bold text-black/70" forceColorClass="text-red-600" />
                  <AmountCell value={rowItem.netAmountSubAdmin} />
                  <AmountCell value={rowItem.shareAmountSubAdmin} />
                  <AmountCell value={rowItem.finalAmountSubAdmin} className="font-bold text-black/70" />
                  <AmountCell value={rowItem.matchCommissionAdmin} forceColorClass="text-red-600" />
                  <AmountCell value={rowItem.sessionCommissionAdmin} forceColorClass="text-red-600" />
                  <AmountCell value={rowItem.totalCommissionAdmin} className="font-bold text-black/70" forceColorClass="text-red-600" />
                  <AmountCell value={rowItem.netAmountAdmin} />
                  <AmountCell value={rowItem.shareAmountAdmin} />
                  <AmountCell value={rowItem.finalAmountAdmin} className="font-bold text-black/70" />
                </tr>
              );
            })}
            <tr className="bg-white text-sm">
              <td className="p-2 w-20 text-center font-bold text-black/70">A. TOTAL</td>
              <AmountCell value={totalsItem.matchAmount} />
              <AmountCell value={totalsItem.sessionAmount} />
              <AmountCell value={totalsItem.totalAmoount} />
              <AmountCell value={totalsItem.netAmountDealer} />
              <AmountCell value={totalsItem.matchCommissionDealer} forceColorClass="text-red-600" />
              <AmountCell value={totalsItem.sessionCommissionDealer} forceColorClass="text-red-600" />
              <AmountCell value={totalsItem.totalCommissionDealer} className="text-black/70 font-semibold" forceColorClass="text-red-600" />
              <AmountCell value={totalsItem.netAmountDealer} />
              <AmountCell value={totalsItem.shareAmountDealer} />
              <AmountCell value={totalsItem.finalAmountDealer} className="text-black/70 font-semibold" />
              <AmountCell value={totalsItem.matchCommissionMaster} forceColorClass="text-red-600" />
              <AmountCell value={totalsItem.sessionCommissionMaster} forceColorClass="text-red-600" />
              <AmountCell value={totalsItem.totalCommissionMaster} className="text-black/70 font-semibold" forceColorClass="text-red-600" />
              <AmountCell value={totalsItem.netAmountMaster} />
              <AmountCell value={totalsItem.shareAmountMaster} />
              <AmountCell value={totalsItem.finalAmountMaster} className="text-black/70 font-semibold" />
              <AmountCell value={getValue(totalsItem, 'matchCommissionSuperMaster')} forceColorClass="text-red-600" />
              <AmountCell value={getValue(totalsItem, 'sessionCommissionSuperMaster')} forceColorClass="text-red-600" />
              <AmountCell value={getValue(totalsItem, 'totalCommissionSuperMaster')} className="text-black/70 font-semibold" forceColorClass="text-red-600" />
              <AmountCell value={getValue(totalsItem, 'netAmountSuperMaster')} />
              <AmountCell value={getValue(totalsItem, 'shareAmountSuperMaster')} />
              <AmountCell value={getValue(totalsItem, 'finalAmountSuperMaster')} className="text-black/70 font-semibold" />
              <AmountCell value={totalsItem.matchCommissionSubAdmin} forceColorClass="text-red-600" />
              <AmountCell value={totalsItem.sessionCommissionSubAdmin} forceColorClass="text-red-600" />
              <AmountCell value={totalsItem.totalCommissionSubAdmin} className="text-black/70 font-semibold" forceColorClass="text-red-600" />
              <AmountCell value={totalsItem.netAmountSubAdmin} />
              <AmountCell value={totalsItem.shareAmountSubAdmin} />
              <AmountCell value={totalsItem.finalAmountSubAdmin} className="text-black/70 font-semibold" />
              <AmountCell value={totalsItem.matchCommissionAdmin} forceColorClass="text-red-600" />
              <AmountCell value={totalsItem.sessionCommissionAdmin} forceColorClass="text-red-600" />
              <AmountCell value={totalsItem.totalCommissionAdmin} className="font-bold text-black/70" forceColorClass="text-red-600" />
              <AmountCell value={totalsItem.netAmountAdmin} />
              <AmountCell value={totalsItem.shareAmountAdmin} />
              <AmountCell value={totalsItem.finalAmountAdmin} className="font-bold text-black/70" />
            </tr>
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="w-full">
      {hasChildren && (
        <div className="border-white w-full">
          {/* Header */}
          <div 
            className={`${headerBgClass} divide-x divide-gray-400 border-b border-gray-400 w-full grid`}
            style={{ gridTemplateColumns: gridTemplateColumns }}
          >
            <span className="py-2 text-sm uppercase text-gray-800" style={{ borderLeft: '1px solid #9ca3af' }}></span>
            <span className="py-2 text-sm uppercase font-bold text-gray-800" style={{ borderLeft: '1px solid #9ca3af' }}>{levelTitle}</span>
            <span className="py-2 text-sm font-semibold capitalize text-gray-900" style={{ borderLeft: '1px solid #9ca3af' }}>{formatDisplayUserId(name)}</span>
          </div>

          {/* Children */}
          {!isAgentLevel && (
            <div className="w-full">
              {ledgerChildren.map(function(child, index) {
                return (
                  <LedgerCard
                    key={index}
                    item={child}
                    level={level + 1}
                  />
                );
              })}
            </div>
          )}

          {/* Total Footer */}
          {level < 4 && (
            <table
              className="plus-table w-full font-bold"
              style={{ minWidth: getTableMinWidth(35) }}
            >
              {renderColGroup(35)}
              <tbody>
                <tr className="bg-white w-full text-sm">
                  <td className="p-2 w-20 text-center font-bold text-black/70">
                    {totalLabels[level] || 'A. TOTAL'}
                  </td>
                <AmountCell value={item.matchAmount} />
                <AmountCell value={item.sessionAmount} />
                <AmountCell value={item.totalAmoount} />
                <AmountCell value={item.netAmountDealer} />
                <AmountCell value={item.matchCommissionDealer} forceColorClass="text-red-600" />
                <AmountCell value={item.sessionCommissionDealer} forceColorClass="text-red-600" />
                <AmountCell value={item.totalCommissionDealer} forceColorClass="text-red-600" />
                <AmountCell value={item.netAmountDealer} className="text-black/70 font-semibold" />
                <AmountCell value={item.shareAmountDealer} />
                <AmountCell value={item.finalAmountDealer} className="text-black/70 font-semibold" />
                <AmountCell value={item.matchCommissionMaster} forceColorClass="text-red-600" />
                <AmountCell value={item.sessionCommissionMaster} forceColorClass="text-red-600" />
                <AmountCell value={item.totalCommissionMaster} forceColorClass="text-red-600" />
                <AmountCell value={item.netAmountMaster} className="text-black/70 font-semibold" />
                <AmountCell value={item.shareAmountMaster} />
                <AmountCell value={item.finalAmountMaster} className="text-black/70 font-semibold" />
                <AmountCell value={getValue(item, 'matchCommissionSuperMaster')} forceColorClass="text-red-600" />
                <AmountCell value={getValue(item, 'sessionCommissionSuperMaster')} forceColorClass="text-red-600" />
                <AmountCell value={getValue(item, 'totalCommissionSuperMaster')} forceColorClass="text-red-600" />
                <AmountCell value={getValue(item, 'netAmountSuperMaster')} className="text-black/70 font-semibold" />
                <AmountCell value={getValue(item, 'shareAmountSuperMaster')} />
                <AmountCell value={getValue(item, 'finalAmountSuperMaster')} className="text-black/70 font-semibold" />
                <AmountCell value={item.matchCommissionSubAdmin} forceColorClass="text-red-600" />
                <AmountCell value={item.sessionCommissionSubAdmin} forceColorClass="text-red-600" />
                <AmountCell value={item.totalCommissionSubAdmin} forceColorClass="text-red-600" />
                <AmountCell value={item.netAmountSubAdmin} className="text-black/70 font-semibold" />
                <AmountCell value={item.shareAmountSubAdmin} />
                <AmountCell value={item.finalAmountSubAdmin} className="text-black/70 font-semibold" />
                <AmountCell value={item.matchCommissionAdmin} forceColorClass="text-red-600" />
                <AmountCell value={item.sessionCommissionAdmin} forceColorClass="text-red-600" />
                <AmountCell value={item.totalCommissionAdmin} forceColorClass="text-red-600" />
                <AmountCell value={item.netAmountAdmin} className="font-bold text-black/70" />
                <AmountCell value={item.shareAmountAdmin} />
                <AmountCell value={item.finalAmountAdmin} className="font-bold text-black/70" />
                </tr>
              </tbody>
            </table>
          )}
        </div>
      )}
      
      {/* Client table */}
      {(isClient || isAgentLevel) && renderClientTable(clientRows, item)}
    </div>
  );
};

const MatchSessionPlusMinusLedgerPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { matchId, matchName } = useParams();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [ledgerData, setLedgerData] = useState(null);

  const selectedData = location.state || {};
  const ledgerItems = ledgerData ? (ledgerData.ledgetList || ledgerData.ledgerList || []) : [];

  useEffect(function() {
    const loadLedger = async function() {
      setLoading(true);
      setError(null);

      const result = await fetchCompleteLedger(
        matchId,
        selectedData.matchCompleted || false,
        selectedData.selectedSessions || [],
        selectedData.selectedUsers || []
      );

      if (result.success) {
        setLedgerData(result.data);
      } else {
        setError(result.error);
      }

      setLoading(false);
    };

    if (matchId) {
      loadLedger();
    }
  }, [matchId, selectedData]);

  return (
    <div className="w-full">
      <div className="flex justify-center">
        <div className="w-full px-0">
          <div className="bg-white rounded shadow border border-gray-200">
            {/* Header */}
            <div className="bg-gray-600 px-5 pt-3 w-full flex items-center justify-between relative z-[2]">
              <span className="text-xl text-white font-normal capitalize pt-1">
                {decodeURIComponent(matchName || 'Match and Session Plus Minus')}
              </span>
              <button 
                onClick={function() { navigate(-1); }}
                className="bg-blue-500 text-white px-4 py-2 hover:bg-blue-600"
              >
                Back
              </button>
            </div>

            {/* Content */}
            <div className="w-full">
              <div className="w-full">
                <div style={{ width: '100%', maxHeight: '70vh', overflow: 'hidden' }}>
                  {loading ? (
                    <div className="flex justify-center py-12">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
                    </div>
                  ) : error ? (
                    <div className="p-4 text-red-500 text-center">{error}</div>
                  ) : ledgerData ? (
                    <TransformWrapper
                      minScale={0.2}
                      maxScale={3}
                      initialScale={1}
                      wheel={{ step: 0.1 }}
                      pinch={{ step: 5 }}
                      panning={{ velocityDisabled: true }}
                      alignmentAnimation={{ sizeX: 0, sizeY: 0 }}
                    >
                      <TransformComponent
                        wrapperStyle={{ width: '100%', height: '70vh', overflow: 'auto' }}
                        contentStyle={{ width: 'max-content', minWidth: '100%' }}
                      >
                        <div className="plus_minus_sec">
                          <table>
                            <tbody>
                              <tr className="w-full">
                                <td colSpan="2">
                                  <div className="w-full">
                                    {ledgerItems.length === 0 ? (
                                      <div className="p-6 text-center text-gray-500">
                                        No ledger data found.
                                      </div>
                                    ) : (
                                      ledgerItems.map(function(item, index) {
                                        return (
                                          <LedgerCard
                                            key={index}
                                            item={item}
                                            level={0}
                                          />
                                        );
                                      })
                                    )}
                                    
                                    {/* Grand Total Footer */}
          <table
            className="plus-table w-full font-bold"
            style={{ minWidth: getTableMinWidth(35) }}
          >
            {renderColGroup(35)}
            <tbody>
                                        <tr className="bg-white w-full">
                                          <td className="p-2 w-20 text-center text-black/70 font-semibold">AA. TOTAL</td>
                                          <AmountCell value={ledgerData.matchAmount} />
                                          <AmountCell value={ledgerData.sessionAmount} />
                                          <AmountCell value={ledgerData.totalAmoount} />
                                          <AmountCell value={ledgerData.netAmountDealer} />
                                          <AmountCell value={ledgerData.matchCommissionDealer} forceColorClass="text-red-600" />
                                          <AmountCell value={ledgerData.sessionCommissionDealer} forceColorClass="text-red-600" />
                                          <AmountCell value={ledgerData.totalCommissionDealer} forceColorClass="text-red-600" />
                                          <AmountCell value={ledgerData.netAmountDealer} className="text-black/70 font-semibold" />
                                          <AmountCell value={ledgerData.shareAmountDealer} />
                                          <AmountCell value={ledgerData.finalAmountDealer} className="text-black/70 font-semibold" />
                                          <AmountCell value={ledgerData.matchCommissionMaster} forceColorClass="text-red-600" />
                                          <AmountCell value={ledgerData.sessionCommissionMaster} forceColorClass="text-red-600" />
                                          <AmountCell value={ledgerData.totalCommissionMaster} className="text-black/70 font-semibold" forceColorClass="text-red-600" />
                                          <AmountCell value={ledgerData.netAmountMaster} />
                                          <AmountCell value={ledgerData.shareAmountMaster} />
                                          <AmountCell value={ledgerData.finalAmountMaster} className="text-black/70 font-semibold" />
                                          <AmountCell value={getValue(ledgerData, 'matchCommissionSuperMaster')} forceColorClass="text-red-600" />
                                          <AmountCell value={getValue(ledgerData, 'sessionCommissionSuperMaster')} forceColorClass="text-red-600" />
                                          <AmountCell value={getValue(ledgerData, 'totalCommissionSuperMaster')} className="text-black/70 font-semibold" forceColorClass="text-red-600" />
                                          <AmountCell value={getValue(ledgerData, 'netAmountSuperMaster')} />
                                          <AmountCell value={getValue(ledgerData, 'shareAmountSuperMaster')} />
                                          <AmountCell value={getValue(ledgerData, 'finalAmountSuperMaster')} className="text-black/70 font-semibold" />
                                          <AmountCell value={ledgerData.matchCommissionSubAdmin} forceColorClass="text-red-600" />
                                          <AmountCell value={ledgerData.sessionCommissionSubAdmin} forceColorClass="text-red-600" />
                                          <AmountCell value={ledgerData.totalCommissionSubAdmin} className="text-black/70 font-semibold" forceColorClass="text-red-600" />
                                          <AmountCell value={ledgerData.netAmountSubAdmin} />
                                          <AmountCell value={ledgerData.shareAmountSubAdmin} />
                                          <AmountCell value={ledgerData.finalAmountSubAdmin} className="text-black/70 font-semibold" />
                                          <AmountCell value={ledgerData.matchCommissionAdmin} forceColorClass="text-red-600" />
                                          <AmountCell value={ledgerData.sessionCommissionAdmin} forceColorClass="text-red-600" />
                                          <AmountCell value={ledgerData.totalCommissionAdmin} className="text-black/70 font-semibold" forceColorClass="text-red-600" />
                                          <AmountCell value={ledgerData.netAmountAdmin} />
                                          <AmountCell value={ledgerData.shareAmountAdmin} />
                                          <AmountCell value={ledgerData.finalAmountAdmin} className="text-black/70 font-semibold" />
                                        </tr>
                                      </tbody>
                                    </table>
                                  </div>
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </TransformComponent>
                    </TransformWrapper>
                  ) : null}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MatchSessionPlusMinusLedgerPage;
